/* eslint-disable @typescript-eslint/no-use-before-define */
import isEqual from 'lodash/isEqual';
import React, {useState} from 'react';

import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {Directions} from 'common/components/drop-menu/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ChangeEvent} from 'common/components/select/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {renameEvent} from 'common/query-builder/cohorts-query-builder/util';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {MetricType, SelectorType} from 'common/query-builder/query-block/constants';
import {
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
} from 'common/query-builder/query-entry-section/constants';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {
  Action,
  CountBy,
  EntryPart,
  EntrySectionUpdates,
  FiltersOperator,
  isMetricEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryEventEntry,
  QuerySuperEntry,
  RetentionCountType,
  RowLabelType,
} from 'common/query-builder/query-entry-section/types';
import {ChartType, CountUsersOnceState, ReportName, ResourceType, RETENTION_MATH_TYPES} from 'common/report/constants';
import {
  RetentionAlignmentUpdate,
  RetentionBucketSizeUpdate,
  RetentionCriteriaReact,
  RetentionUnitUpdate,
} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/retention-criteria';
// eslint-disable-next-line import/no-cycle
import {QueryEntrySectionReact} from 'common/query-builder/query-entry-section';
import {EditControls} from 'common/query-builder/query-entry-section/components/edit-controls';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {getRetentionMetricAttributionItems} from 'common/report/insights/util/retention-attribution';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getEditCapability} from 'common/report/util/saved-entities';
import {MultiAttributionMenu} from 'common/widgets/multi-attribution-menu';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {MultiAttributionType} from 'common/widgets/multi-attribution-menu/types';
import {
  getMetricEntryBehavior,
  isModifiedEntryBehavior,
  isSavedBehavior,
  isUnsavedBehavior,
} from 'common/report/insights/util/events';
import {MetricBehavior} from 'common/report/insights/util/types';
import {MenuType} from 'common/query-builder/query-block/types';
import {RowSelector} from 'common/query-builder/query-entry-section/components/selector';
import {Behavior} from 'app_api/projects/behaviors/types';
import {getCountUsersOnceTypeFromEntrySelection} from 'common/query-builder/query-block/count-menu/util';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {
  getMultiAttributionFromEntry,
  isSupportedMultiAttributionTypeForEntry,
} from 'common/query-builder/query-builder/util';
import {
  getMultiAttributionLabel,
  getMultiAttributionTitle,
  getUpdatedMultiAttribution,
} from 'common/query-builder/query-entry-section/util/multi-attribution';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {setEntryVisibility, toggleExpandedRow} from 'common/query-builder/query-entry-section/row-types/row-util';
import {TrackingEvent} from 'common/query-builder/query-entry-section/tracking';

import {EditableState, EditState, getTrackingProps, hasValidBehavior, saveBehavior} from '../behavior-utils';
import {canSaveForPlan, getNewEntry, hasSavedRetentionBehaviors} from './utils';
import {helpers} from './helpers';
import {RetentionQuerySettingsReact, RetentionQuerySettingsUpdate} from './retention-query-settings';

export function RetentionMetricEntryRow(props: EntryRowProps<MetricEntry>) {
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const [isCreatingInline, setIsCreatingInline] = useState<boolean>(false);

  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();

  const isPlaceholder = helpers.isPlaceholder(props);

  const track = (event: TrackingEvent, extraProps: Obj = {}) => {
    props.dispatchTrack(event, {
      ...props.getEntryTrackingProps(),
      ...extraProps,
    });
  };

  const getEditState = (): EditState => {
    if (!canSaveForPlan(currentProject.get().value)) {
      return {
        showEditControls: false,
        saveDisabledMessage: `Cannot save retention metric with your current plan. [Learn more](https://mixpanel.com/pricing/).`,
      };
    }

    const isSaving = behaviors.isLoading(props.entry.behavior.id);
    let canEdit = false;
    let canSave = true;
    let isSaved = false;
    let saveDisabledMessage = ``;
    if (isSavedBehavior(props.entry)) {
      const behavior = behaviors.getOne(props.entry.behavior.id).value;
      canEdit = getEditCapability(behavior).enableEdit;
      isSaved = true;
      if (
        // behavior does not exists if the behavior is created and not shared by others
        behavior &&
        !isModifiedEntryBehavior({behavior, entryBehavior: props.entry.behavior})
      ) {
        canSave = false;
        saveDisabledMessage = `No unsaved changes`;
      }
    }
    if (!hasValidBehavior(props.entry.behavior)) {
      canSave = false;
      saveDisabledMessage = `Cannot save incomplete behavior`;
    }

    return {
      showEditControls: true,
      canEdit,
      canSave,
      isSaved,
      isSaving,
      saveDisabledMessage,
    };
  };

  const handleControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.Save: {
        const saveAsNew = isUnsavedBehavior(props.entry);
        handleSaveBehavior(saveAsNew, !saveAsNew);
        break;
      }
      case ControlBarAction.Show:
      case ControlBarAction.Hide:
        setEntryVisibility(props, value.action === ControlBarAction.Show);
        break;
      default:
        props.onControlSelect(value);
    }
  };

  const handleSelectSavedBehavior = (
    behavior: Behavior,
    {isSaveExisting = false, isExpanded = false}: {isSaveExisting?: boolean; isExpanded?: boolean} = {},
  ) => {
    props.updateEntry(
      isSaveExisting ? Action.SaveBehavior : Action.Edit,
      {
        ...props.entry,
        isExpanded,
        behavior: getMetricEntryBehavior({
          ...behavior.definition.behavior,
          type: behavior.type,
          name: behavior.name,
          id: behavior.id,
        } as MetricBehavior),
        hasUnsavedChanges: false,
      },
      {
        requery: true,
      },
    );
  };
  const handleCreateInline = () => {
    setIsCreatingInline(true);
    props.updateEntry(Action.Edit, getNewEntry({type: MetricType.Retention, isExpanded: true}));
    props.setActivePart(EntryPart.SubValue, `open`);
  };

  const handleSaveBehavior = async (saveAsNew = false, previouslyExisting = false) => {
    const {behavior} = props.entry;
    const {isCreating, results} = await saveBehavior({
      behavior,
      behaviors,
      currentProject,
      saveAsNew,
      previouslyExisting,
    });
    if (!results) {
      return;
    }

    const newBehavior = Object.values(results)[0];
    if (isCreating) {
      track(TrackingEvent.SaveBehavior, {
        'Save as New': saveAsNew,
        ...getTrackingProps(props.entry),
      });
    } else {
      track(TrackingEvent.SaveBehaviorChanges, {...getTrackingProps(props.entry)});
    }
    handleSelectSavedBehavior(newBehavior, {isSaveExisting: !saveAsNew});
  };

  const handleReset = () => {
    if (isSavedBehavior(props.entry)) {
      track(TrackingEvent.ResetBehavior, {...getTrackingProps(props.entry)});
      const behavior = behaviors.getOne(props.entry.behavior.id).value;
      handleSelectSavedBehavior(behavior, {isExpanded: true});
    }
  };

  const handleDeleteEntry = () => {
    if (props.sectionProps.reportName === ReportName.Retention) {
      // reset to default state if it's /app/retention
      props.updateEntry(Action.Edit, getNewEntry({type: props.entry.behavior.type, isExpanded: true}));
      return;
    }
    props.removeEntry();
  };

  const suffixRenderer = () => (
    <>
      {props.entry.isExpanded && (
        <>
          <BehaviorBlock {...props} />
          {(() => {
            const editState = getEditState();
            if (props.entry.behavior.type === MetricType.Retention && editState.showEditControls) {
              return (
                <EditControlsBlock
                  entry={props.entry}
                  handleSaveBehavior={handleSaveBehavior}
                  handleReset={handleReset}
                  editableState={editState}
                />
              );
            }
            return null;
          })()}
        </>
      )}
      <MeasurementBlock {...props} />
    </>
  );

  const actionsRenderer = () => {
    return (
      <ButtonReact
        className='expand-control'
        icon={props.entry.isExpanded ? `collapse-diagonal` : `expand`}
        theme={ButtonTheme.Tertiary}
        size={ButtonSize.Medium}
        tooltip={{
          text: props.entry.isExpanded ? `Collapse` : `Expand`,
        }}
        onClick={() => toggleExpandedRow(props)}
      />
    );
  };

  const selectorRenderer = () => {
    let metricName =
      props.entry.behavior.type === MetricType.RetentionFrequency
        ? `Untitled`
        : isCreatingInline
          ? `New Retention Behavior`
          : `Select Retention Behavior`;
    let isSaved = isSavedBehavior(props.entry);
    if (isSaved) {
      metricName = props.entry.behavior.name || metricName;
      const behavior = behaviors.getOne(props.entry.behavior.id).value;
      isSaved = behavior ? !isModifiedEntryBehavior({behavior, entryBehavior: props.entry.behavior}) : false;
    } else {
      metricName = props.entry.behavior.renamed || props.entry.behavior.name || metricName;
    }

    if (
      props.entry.behavior.type === MetricType.RetentionFrequency ||
      (!canSaveForPlan(currentProject.get().value) && !hasSavedRetentionBehaviors({behaviors}))
    ) {
      return (
        <div className='mp-metric-entry-header-name' title={metricName}>
          <span className='metric-name'>{metricName}</span>
        </div>
      );
    }

    return (
      <div className={`mp-metric-entry-header-selector ${isSaved ? `is-saved` : ``}`}>
        <RowSelector
          {...props}
          contextPanelParentElement={entryEl}
          isPlaceholder={isPlaceholder}
          selected={{
            id: props.entry.behavior.id,
            name: metricName,
          }}
          queryBlockConfig={{
            type: `behaviors`,
            metricType: MetricType.Retention,
          }}
          handleSelect={(selection: {item: Behavior}) => {
            track(TrackingEvent.SelectBehavior, {
              ...getTrackingProps(props.entry),
            });
            handleSelectSavedBehavior(selection.item);
          }}
          handleCreate={() => handleCreateInline()}
        />
      </div>
    );
  };

  const {saveDisabledMessage} = getEditState();
  return (
    <BaseEntryRow
      ref={setEntryEl}
      {...props}
      isPlaceholder={isPlaceholder}
      showLabels={props.sectionProps.reportName === ReportName.Insights}
      extraEntryClass={{
        'is-metric': true,
      }}
      renderSelector={selectorRenderer}
      renderSuffix={suffixRenderer}
      renderActions={actionsRenderer}
      onControlSelect={handleControlSelect}
      canAlwaysDelete
      onDeleteEntry={handleDeleteEntry}
      overflowControls={[
        ...props.getCommonOverflowControls(),
        ControlBarActionItems[ControlBarAction.Rename],
        props.entry.behavior.type === MetricType.Retention && {
          ...ControlBarActionItems[ControlBarAction.Save],
          ...(saveDisabledMessage
            ? {
                isDisabled: true,
                tooltip: {
                  text: saveDisabledMessage,
                },
              }
            : {}),
        },
        ...(props.sectionProps.reportName === ReportName.Insights
          ? [
              ControlBarActionItems[ControlBarAction.Delete],
              ControlBarActionItems[ControlBarAction.Duplicate],
              props.sectionProps.enableHideEntry &&
                ControlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],
            ]
          : []),
      ].filter(Boolean)}
    />
  );
}

function MeasurementBlock(props: EntryRowProps<MetricEntry>) {
  const metrics = useMetrics();
  const behaviors = useBehaviors();

  const handleRetentionCountBySelected = (detail: {item: CountBy}) => {
    const countBy = detail.item;
    const measurement = props.entry.measurement;

    measurement.aggregationOperator = countBy.aggregationOperator;
    measurement.countUsersOnceType = getCountUsersOnceTypeFromEntrySelection({
      selectedCountUsersOnceType: countBy.countUsersOnceType,
      metricType: MetricType.Retention,
      multiAttribution: measurement.multiAttribution,
    });
    measurement.property = countBy.property;

    const entry = {
      ...props.entry,
      measurement,
    };
    props.updateEntry(Action.Edit, entry);
  };

  const handleRetentionBucketIndexSelected = (ev: ChangeEvent<number>) => {
    const retentionBucketIndex = ev.detail.item.value;
    const entry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        retentionBucketIndex,
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const handleAttributionSelection = ({detail}: CustomEvent) => {
    const entry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        attributionSelection: detail.item.value,
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const breakdownAttributionLabel = `${props.sectionProps.breakdownAttributionLabelPrefix} on ${
    getRetentionMetricAttributionItems(props.entry).find((entry) => entry.isSelected)?.label || `...`
  }`;
  const multiAttribution = getMultiAttributionFromEntry(props.entry);
  const isMultiAttributionOpen = props.activePart === EntryPart.MultiAttribution;
  const COUNT_TYPE_MAPPING = {
    [RETENTION_MATH_TYPES.RETENTION_RATE]: RetentionCountType.RetentionRate,
    [RETENTION_MATH_TYPES.UNIQUE]: RetentionCountType.Unique,
    [RETENTION_MATH_TYPES.PROPERTY_AVERAGE]: RetentionCountType.PropertyAverage,
    [RETENTION_MATH_TYPES.PROPERTY_SUM]: RetentionCountType.PropertySum,
  };

  return (
    <div className='mp-metric-entry-measurement'>
      <div className='mp-metric-entry-measurement-inner'>
        <QueryBlockReact
          className='count-menu'
          elref='countBlock'
          type={MenuType.Count}
          metricType={props.entry.behavior.type}
          dataGroupId={props.entry.behavior.dataGroupId || ``}
          dataGroups={Object.values(dataGroupStore.value)}
          projectId={props.sectionProps.projectId}
          selectorType={SelectorType.Button}
          countUsersOnceState={
            props.sectionProps.canUseMultiAttribution
              ? CountUsersOnceState.Required
              : props.sectionProps.countUsersOnceState
          }
          selected={{
            countType: COUNT_TYPE_MAPPING[props.entry.measurement.aggregationOperator],
            aggregationOperator: props.entry.measurement.aggregationOperator,
            property: props.entry.measurement.property,
            countUsersOnceType: props.entry.measurement.countUsersOnceType,
          }}
          highlightSelections
          theme={ButtonTheme.QueryBuilder}
          buttonSize={ButtonSize.Small}
          trackName='retention'
          userId={props.sectionProps.userId}
          workspaceId={props.sectionProps.workspaceId}
          onSelect={handleRetentionCountBySelected}
        />

        {props.sectionProps.chartType === ChartType.RetentionCurve ? (
          <SelectReact
            alwaysShowCaret
            theme={SelectTheme.Tertiary}
            size={ButtonSize.Small}
            menuAlignment={Directions.Right}
            menuWidth={184}
            disableFirstItemPadding
            elref='retentionBucketIndexSelect'
            sections={[
              {
                label: `Retention groups`,
                icon: `info-xs`,
                tooltip: {
                  'max-width': `120px`,
                  placement: Placement.Right,
                  text: `View how specific Retention Groups trend over time. [Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-trends)`,
                },
                items: [
                  {
                    label: `All Groups`,
                    isSelected: true,
                    value: null,
                  },
                ],
              },
            ]}
          />
        ) : (
          <SelectReact
            alwaysShowCaret
            theme={SelectTheme.Tertiary}
            size={ButtonSize.Small}
            menuAlignment={Directions.Right}
            menuWidth={184}
            disableFirstItemPadding
            elref='retentionBucketIndexSelect'
            sections={[
              {
                label: `Retention groups`,
                icon: `info-xs`,
                tooltip: {
                  'max-width': `120px`,
                  placement: Placement.Right,
                  text: `View how specific Retention Groups trend over time. [Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-trends)`,
                },
                items: props.entry.measurement.retentionBuckets,
              },
            ]}
            onChange={handleRetentionBucketIndexSelected}
          />
        )}
      </div>

      {!props.sectionProps.canUseMultiAttribution && props.sectionProps.canUseBreakdownAttribution && (
        <div className='mp-metric-entry-measurement-inner'>
          <SelectReact
            elref='attributionSelect'
            alwaysShowCaret
            sections={[{items: getRetentionMetricAttributionItems(props.entry)}]}
            label={breakdownAttributionLabel}
            selectedOption=''
            size={ButtonSize.Small}
            theme={SelectTheme.Tertiary}
            menuWidth={0}
            tabIndex={0}
            experimentalPositioning
            onChange={handleAttributionSelection}
          />
        </div>
      )}
      {props.sectionProps.canUseMultiAttribution && multiAttribution && (
        <div className='mp-metric-entry-measurement-inner'>
          <SelectReact
            className='subitem-select.multi-attribution-type'
            elref='multiAttributionSelector'
            alwaysShowCaret
            label={getMultiAttributionLabel(props.entry, isMultiAttributionOpen)}
            title={isMultiAttributionOpen ? `` : getMultiAttributionTitle(props.entry)}
            open={isMultiAttributionOpen}
            theme={SelectTheme.Tertiary}
            size={ButtonSize.Small}
            onMenuChange={(ev) => props.setActivePart(EntryPart.MultiAttribution, ev.detail.isOpen ? `open` : `close`)}
            tooltip={{
              text: `${getMultiAttributionTitle(props.entry)} ${breakdownAttributionLabel}.`,
              size: DisplaySize.Medium,
            }}
          >
            <div slot='body'>
              {isMultiAttributionOpen && (
                <MultiAttributionMenu
                  onCancel={() => props.setActivePart(EntryPart.MultiAttribution, `close`)}
                  onChange={(multiAttribution) =>
                    props.updateEntry(Action.Edit, getUpdatedMultiAttribution(props.entry, multiAttribution))
                  }
                  onDelete={() => props.updateEntry(Action.Edit, getUpdatedMultiAttribution(props.entry, null))}
                  isSupportedMultiAttributionType={(type) =>
                    isSupportedMultiAttributionTypeForEntry({
                      entry: props.entry,
                      multiAttributionType: type,
                      metricsResource: metrics,
                      behaviorsResource: behaviors,
                    })
                  }
                  showMultiAttributionType={(type) =>
                    [
                      MultiAttributionType.LastTouch,
                      MultiAttributionType.FirstTouch,
                      MultiAttributionType.Participation,
                    ].includes(type)
                  }
                  metricIndex={props.index}
                  multiAttribution={multiAttribution || DefaultMultiAttribution}
                  projectIsFreePlan={props.sectionProps.projectIsFreePlan}
                />
              )}
            </div>
          </SelectReact>
        </div>
      )}
    </div>
  );
}

function BehaviorBlock(props: EntryRowProps<MetricEntry>) {
  const behaviors = useBehaviors();

  const getCountingMethod = (): string => {
    const {retentionType, retentionUnboundedMode} = props.entry.behavior;
    const {retentionCumulative} = props.entry.measurement;

    if (retentionType === `addiction`) {
      if (retentionCumulative) {
        return `cumulative`;
      }
      return `non-cumulative`;
    }

    if (retentionUnboundedMode === `carry_back`) {
      return `unbounded`;
    } else if (retentionUnboundedMode === `carry_forward`) {
      return `unbounded_forward`;
    }
    return `bounded`;
  };

  const handleEntryUpdate = (updates: EntrySectionUpdates<QueryEventEntry>) => {
    const {entries} = updates;
    const behaviors: MetricEntryBehavior[] = entries.map((entry) => ({
      id: entry.event.custom ? entry.event.id : null,
      type: entry.event.custom ? MetricType.CustomEvent : MetricType.Event,
      name: entry.event.value,
      filters: entry.filters,
      filtersDeterminer: entry.filtersOperator === FiltersOperator.Or ? `any` : `all`,
    }));
    const entry: MetricEntry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior: {
        ...props.entry.behavior,
        behaviors,
      },
    };

    // need to pass in `getEntries(true)` because we maybe editing the stagingRow
    // After drag and drop, index can change
    const index = props.getEntries(true).findIndex((el) => el.entryKey === entry.entryKey);
    props.updateEntry(Action.Edit, entry, {index});
  };

  const handleSettingsChanged = (update: RetentionQuerySettingsUpdate) => {
    const entry: MetricEntry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior: {
        ...props.entry.behavior,
        retentionUnboundedMode: update.detail.unboundedMode,
      },
      measurement: {
        ...props.entry.measurement,
        retentionCumulative: Boolean(update.detail.retentionCumulative),
      },
    };
    props.updateEntry(Action.Edit, entry);

    // TODO: emit track event
  };

  const handleRetentionUnitChanged = (update: RetentionUnitUpdate) => {
    const {retentionUnit} = update.detail;

    // TODO(CC): When the chart type is retention curve, changing the retention unit or bucket sizes
    // of a retention metric changes the retention units of all the other existing retention metrics.
    // Tune this later when we find a better solution to handle this.
    if (props.currentChartType === ChartType.RetentionCurve) {
      const entries = props.getEntries();
      entries.forEach((entry, index) => {
        if (isMetricEntry(entry) && entry.behavior.type === MetricType.Retention) {
          const updatedEntry: MetricEntry = {
            ...entry,
            hasUnsavedChanges: true,
            behavior: {
              ...entry.behavior,
              retentionUnit,
              retentionCustomBucketSizes: [],
            },
            measurement: {
              ...entry.measurement,
              // if retention unit changed, set bucket index to 0
              ...(!isEqual(entry.behavior.retentionUnit, retentionUnit) ? {retentionBucketIndex: 0} : {}),
            },
          };
          props.updateEntry(Action.Edit, updatedEntry, {index});
        }
      });
      return;
    }

    const entry: MetricEntry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior: {
        ...props.entry.behavior,
        retentionUnit,
        retentionCustomBucketSizes: [],
      },
      measurement: {
        ...props.entry.measurement,
        // if retention unit changed, set bucket index to 0
        ...(!isEqual(props.entry.behavior.retentionUnit, retentionUnit) ? {retentionBucketIndex: 0} : {}),
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const handleBucketSizesApplied = (update: RetentionBucketSizeUpdate) => {
    const {retentionUnit, bucketSizes} = update.detail;

    // TODO(CC): When the chart type is retention curve, changing the retention unit or bucket sizes
    // of a retention metric changes the retention units of all the other existing retention metrics.
    // Tune this later when we find a better solution to handle this.
    if (props.currentChartType === ChartType.RetentionCurve) {
      const entries = props.getEntries();
      entries.forEach((entry, index) => {
        if (isMetricEntry(entry) && entry.behavior.type === MetricType.Retention) {
          const updatedEntry: MetricEntry = {
            ...entry,
            hasUnsavedChanges: true,
            behavior: {
              ...entry.behavior,
              retentionUnit,
              retentionCustomBucketSizes: bucketSizes,
            },
            measurement: {
              ...entry.measurement,
              // if retention unit changed, set bucket index to 0
              ...(!isEqual(entry.behavior.retentionUnit, retentionUnit) ? {retentionBucketIndex: 0} : {}),
            },
          };
          props.updateEntry(Action.Edit, updatedEntry, {index});
        }
      });
      return;
    }

    const entry: MetricEntry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior: {
        ...props.entry.behavior,
        retentionUnit,
        retentionCustomBucketSizes: bucketSizes,
      },
      measurement: {
        ...props.entry.measurement,
        // if retention unit changed, set bucket index to 0
        ...(!isEqual(props.entry.behavior.retentionUnit, retentionUnit) ? {retentionBucketIndex: 0} : {}),
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const handleToggleRetentionAlignment = (update: RetentionAlignmentUpdate) => {
    const {retentionAlignmentType} = update.detail;

    const entry: MetricEntry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior: {
        ...props.entry.behavior,
        retentionAlignmentType,
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const entries = props.entry.behavior.behaviors.map(
    (behavior) =>
      ({
        entryIndexLabelToggle: props.entry.behavior.entryIndexLabelToggle,
        event: {
          custom: behavior.type === MetricType.CustomEvent,
          id: behavior.id,
          label: renameEvent(behavior.name) || ``,
          renamed: behavior.renamed,
          value: behavior.name,
        },
        filters: behavior.filters,
        filtersOperator: behavior.filtersDeterminer === `any` ? FiltersOperator.Or : FiltersOperator.And,
        type: ResourceType.Event,
      }) as QuerySuperEntry,
  );

  const isPlaceholder = helpers.isPlaceholder(props);

  return (
    <div className='mp-metric-entry-behavior'>
      <QueryEntrySectionReact
        key='retentionEntries'
        elref='metricEntry'
        {...props.nestedSectionParams}
        autoactivatePlaceholders={
          props.sectionProps.reportName !== ReportName.Insights
            ? false
            : isPlaceholder || !hasSavedRetentionBehaviors({behaviors})
        }
        allowCohortFilter={false}
        emptyPlaceholder={{
          entryIndexLabelToggle: props.entry.behavior.entryIndexLabelToggle,
          event: {
            custom: false,
            id: null,
            label: ``,
            renamed: null,
            value: null,
          },
          filters: [],
          filtersOperator: FiltersOperator.And,
          type: ResourceType.Event,
        }}
        disableAddEntry
        minEntries={props.entry.behavior.type === MetricType.Retention ? 2 : 1}
        rowDraggable
        rowIndex={props.index}
        rowLimit={props.entry.behavior.type === MetricType.Retention ? 2 : 1}
        rowTypes={[ResourceType.Event]}
        rowLabelType={RowLabelType.Numeric}
        reportName={ReportName.Retention}
        showAllEvents={false}
        showAnyEvent
        showTopEvents={false}
        showSessions={false}
        otherFilterOptions={[]}
        entries={entries}
        onUpdate={handleEntryUpdate}
      />
      <div className='criteria'>
        {props.entry.behavior.type === MetricType.Retention ? (
          <RetentionCriteriaReact
            unboundedMode={props.entry.behavior.retentionUnboundedMode}
            retentionUnit={props.entry.behavior.retentionUnit}
            bucketSizes={props.entry.behavior.retentionCustomBucketSizes}
            retentionAlignmentType={props.entry.behavior.retentionAlignmentType}
            dataGroupId={props.entry.behavior.dataGroupId}
            onChangeSetting={handleSettingsChanged}
            onChangeUnit={handleRetentionUnitChanged}
            onApplyBucketSizes={handleBucketSizesApplied}
            onToggleRetentionAlignment={handleToggleRetentionAlignment}
          />
        ) : (
          <RetentionQuerySettingsReact
            retentionType={props.entry.behavior.retentionType}
            countingMethod={getCountingMethod()}
            onChange={handleSettingsChanged}
          />
        )}
      </div>
    </div>
  );
}

function EditControlsBlock(props: {
  entry: MetricEntry;
  handleReset: () => void;
  handleSaveBehavior: (saveAsNew: boolean, previouslyExisting: boolean) => void;
  editableState: EditableState;
}) {
  const {canEdit, canSave, isSaved, isSaving, saveDisabledMessage} = props.editableState;

  return (
    <div className='mp-metric-entry-edit-controls'>
      <EditControls
        resetTooltip='Reverts the behavior to its previously saved state'
        isSaved={isSaved}
        isSaving={isSaving}
        canSave={canSave}
        canEdit={canEdit}
        canReset={canSave && isSavedBehavior(props.entry)}
        saveDisabledMessage={saveDisabledMessage}
        entityType='Behavior'
        onReset={props.handleReset}
        onSave={() => props.handleSaveBehavior(false, isSavedBehavior(props.entry))}
        onClone={() => props.handleSaveBehavior(true, isSavedBehavior(props.entry))}
      />
    </div>
  );
}
